import React, { useEffect, useRef, useState, useCallback } from 'react';
import { flattenToAppURL } from '@plone/volto/helpers/Url/Url';

const DUR = 3000;
const still = (it) => {
  const im = Array.isArray(it?.image) ? it.image[0] : it?.image;
  const id = im?.['@id'];
  return id ? `${flattenToAppURL(id)}/@@images/image/great` : null;
};

const Rotator = ({ items = [] }) => {
  const n = items.length;
  const [cur, setCur] = useState(0);
  const [reduce, setReduce] = useState(true); // SSR + reduced motion: posters only
  const paused = useRef(false);
  const vids = useRef([]);
  const x0 = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduce(mq.matches);
    const on = (e) => setReduce(e.matches);
    mq.addEventListener?.('change', on);
    return () => mq.removeEventListener?.('change', on);
  }, []);

  const go = useCallback((i) => setCur(((i % n) + n) % n), [n]);
  const restart = useCallback(() => {
    clearInterval(timer.current);
    if (!paused.current && n > 1) timer.current = setInterval(() => setCur((c) => (c + 1) % n), DUR);
  }, [n]);

  useEffect(() => {
    restart();
    const vis = () => (document.hidden ? clearInterval(timer.current) : restart());
    document.addEventListener('visibilitychange', vis);
    return () => { clearInterval(timer.current); document.removeEventListener('visibilitychange', vis); };
  }, [restart]);

  useEffect(() => {
    vids.current.forEach((v, k) => {
      if (!v) return;
      if (k === cur) { try { v.currentTime = 0; } catch (e) {} const p = v.play(); if (p && p.catch) p.catch(() => {}); }
      else setTimeout(() => { if (v) v.pause(); }, 1200);
    });
  }, [cur, reduce]);

  if (!n) return null;
  const nav = (i) => { go(i); restart(); };
  return (
    <div
      className="gf-rot"
      role="region"
      aria-roledescription="carousel"
      aria-label="Gift moments"
      tabIndex={0}
      onMouseEnter={() => { paused.current = true; clearInterval(timer.current); }}
      onMouseLeave={() => { paused.current = false; restart(); }}
      onKeyDown={(e) => { if (e.key === 'ArrowRight') nav(cur + 1); if (e.key === 'ArrowLeft') nav(cur - 1); }}
    >
      <div
        className="gf-rot-stage"
        onTouchStart={(e) => { x0.current = e.touches[0].clientX; }}
        onTouchEnd={(e) => {
          if (x0.current === null) return;
          const dx = e.changedTouches[0].clientX - x0.current;
          if (Math.abs(dx) > 40) nav(cur + (dx < 0 ? 1 : -1));
          x0.current = null;
        }}
      >
        {items.map((it, i) => {
          const base = it.video ? `/media/feelings/${it.video}` : null;
          const poster = base ? `${base}-poster.jpg` : still(it);
          return (
            <div key={it['@id'] || i} className={`gf-rot-slide${i === cur ? ' on' : ''}`} aria-hidden={i !== cur}>
              {base && !reduce ? (
                <video ref={(el) => (vids.current[i] = el)} muted playsInline loop preload={i === 0 ? 'auto' : 'metadata'} poster={poster} aria-label={it.alt || it.caption}>
                  <source src={`${base}.webm`} type="video/webm" />
                  <source src={`${base}.mp4`} type="video/mp4" />
                </video>
              ) : (
                <img src={poster} alt={it.alt || it.caption || ''} loading={i ? 'lazy' : 'eager'} />
              )}
            </div>
          );
        })}
      </div>
      <div className="gf-rot-caps" aria-live="polite">
        {items.map((it, i) => (
          <p key={i} className={`gf-rot-cap${i === cur ? ' on' : ''}`}>{it.caption}</p>
        ))}
      </div>
      <div className="gf-rot-dots">
        {items.map((it, i) => (
          <button key={i} type="button" aria-label={`Show image ${i + 1} of ${n}`} aria-current={i === cur} onClick={() => nav(i)} />
        ))}
      </div>
    </div>
  );
};
export default Rotator;
